import { describeError, useApi } from '@classgo/app-kit';
import type { GradesView } from '@classgo/shared';
import { Button, EmptyState, Select } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

import { refusalOf } from '../../attendance/api';
import { gradebookApi, gradebookQueries } from '../../gradebook/api';
import type { GradesCell } from '../../gradebook/types';
import { useT } from '../../i18n';
import { recordQueries } from '../../records/api';
import {
  decimalsOf,
  formatScore,
  nextRowFor,
  parseScore,
  readingName,
  scoreIssue,
  studentName,
} from './support';

/**
 * The scores of one evaluation, as the app's teacher screen enters them (#150) but for a
 * keyboard: one box per student with the scale's precision, Enter or Tab move down the column,
 * one "Guardar notas" sends every box in one call (empty clears the score) and the period grade
 * beside each student is the one the API recomputed. A score outside the scale, or with more
 * decimals than it uses, is flagged in its row before anything is sent; the API's own refusal
 * (a published subject) shows in red with its message.
 */
const Grid = ({
  cell,
  view,
  evaluationId,
  evaluationName,
}: {
  cell: GradesCell;
  view: GradesView;
  evaluationId: string;
  evaluationName: string;
}) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const decimals = decimalsOf(view.scale);
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<number | null>(null);
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    setTyped(
      Object.fromEntries(
        view.rows.map((row) => {
          const score = row.scores[evaluationId];
          return [
            row.studentId,
            score === null || score === undefined ? '' : formatScore(score, decimals),
          ];
        }),
      ),
    );
  }, [view, evaluationId, decimals]);
  const save = useMutation({
    mutationFn: () =>
      gradebookApi(api).putScores(evaluationId, {
        scores: view.rows.map((row) => ({
          studentId: row.studentId,
          score: parseScore(typed[row.studentId] ?? ''),
        })),
      }),
    onSuccess: async (book) => {
      queries.setQueryData(
        recordQueries.grades(api, cell.groupId, cell.subjectId, cell.periodId).queryKey,
        book,
      );
      setSaved(book.rows.filter((row) => (row.scores[evaluationId] ?? null) !== null).length);
      await queries.invalidateQueries({ queryKey: ['school', 'evaluations'] });
    },
  });
  const bounds = {
    min: formatScore(view.scale?.minScore ?? null, decimals),
    max: formatScore(view.scale?.maxScore ?? null, decimals),
    decimals,
  };
  const issues = view.rows.map((row) => scoreIssue(typed[row.studentId] ?? '', view.scale));
  const invalid = issues.filter((issue) => issue !== null).length;
  const onKey = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    const target = nextRowFor(event, index, view.rows.length);
    if (target === null) return;
    event.preventDefault();
    boxes.current[target]?.focus();
    boxes.current[target]?.select();
  };

  if (view.rows.length === 0) return <EmptyState title={t('grades.view.empty')} />;
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">{t('gradebook.scores.hint', bounds)}</p>
      <div className="overflow-x-auto">
        <table
          className="w-full text-sm"
          aria-label={t('gradebook.scores.caption', { evaluation: evaluationName })}
        >
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="pb-2 pr-3 font-semibold">{t('grades.view.column.student')}</th>
              <th className="pb-2 pr-3 text-right font-semibold">
                {t('gradebook.scores.column.periodGrade')}
              </th>
              <th className="pb-2 font-semibold">{t('gradebook.scores.column.score')}</th>
            </tr>
          </thead>
          <tbody>
            {view.rows.map((row, index) => {
              const issue = issues[index] ?? null;
              const id = `score-${row.studentId}`;
              return (
                <tr key={row.studentId} className="border-t border-gray-100">
                  <td className="py-1 pr-3 font-medium text-gray-900">{studentName(row)}</td>
                  <td
                    className={`py-1 pr-3 text-right tabular-nums ${view.scale && row.periodGrade !== null && row.periodGrade < view.scale.passMark ? 'text-danger-700' : 'text-gray-600'}`}
                  >
                    {formatScore(row.periodGrade, decimals)}
                  </td>
                  <td className="py-1">
                    <label htmlFor={id} className="sr-only">
                      {t('gradebook.scores.scoreOf', { student: readingName(row) })}
                    </label>
                    <input
                      id={id}
                      ref={(element) => {
                        boxes.current[index] = element;
                      }}
                      inputMode="decimal"
                      autoComplete="off"
                      aria-invalid={issue ? true : undefined}
                      aria-describedby={issue ? `${id}-issue` : undefined}
                      className={`control-touch w-24 rounded-control border px-3 py-2 text-right text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${issue ? 'border-danger-600' : 'border-gray-300'}`}
                      value={typed[row.studentId] ?? ''}
                      onChange={(event) => {
                        setSaved(null);
                        setTyped((current) => ({
                          ...current,
                          [row.studentId]: event.target.value,
                        }));
                      }}
                      onKeyDown={(event) => onKey(event, index)}
                    />
                    {issue ? (
                      <p id={`${id}-issue`} className="mt-1 text-xs text-danger-700">
                        {t(`gradebook.scores.issue.${issue}`, bounds)}
                      </p>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {invalid > 0 ? (
        <p role="alert" className="text-sm text-danger-700">
          {t('gradebook.scores.invalid', { count: invalid })}
        </p>
      ) : null}
      {saved !== null ? (
        <p
          role="status"
          className="rounded-control bg-success-100 px-3 py-2 text-sm text-success-700"
        >
          {t('gradebook.scores.saved', { scored: saved, total: view.rows.length })}
        </p>
      ) : null}
      {save.isError ? (
        <p role="alert" className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700">
          {t('gradebook.scores.failed')} {refusalOf(save.error) ?? describeError(t, save.error)}
        </p>
      ) : null}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button loading={save.isPending} disabled={invalid > 0} onClick={() => save.mutate()}>
          {t('gradebook.scores.save')}
        </Button>
      </div>
    </div>
  );
};

/**
 * The "Digitar notas" tab: the evaluations of the cell to pick one (the first by default) and
 * its grid. Published subjects show the book read-only: the API refuses every write until the
 * school reopens them, so no boxes are drawn.
 */
export const ScoresTab = ({ cell, view }: { cell: GradesCell; view: GradesView }) => {
  const t = useT();
  const api = useApi();
  const evaluations = useQuery(gradebookQueries.evaluations(api, cell));
  const [wanted, setWanted] = useState('');
  const items = evaluations.data?.items ?? [];
  const evaluation = items.find((item) => item.id === wanted) ?? items[0];
  if (evaluations.isError) {
    return (
      <EmptyState
        title={t('entity.error.title')}
        description={describeError(t, evaluations.error)}
      />
    );
  }
  if (!evaluations.data) return <p className="text-sm text-gray-500">{t('common.loading')}</p>;
  if (items.length === 0 || !evaluation) return <EmptyState title={t('gradebook.scores.none')} />;
  if (view.published) {
    return <p className="text-sm text-gray-600">{t('gradebook.state.locked')}</p>;
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Select
          label={t('gradebook.scores.evaluation')}
          value={evaluation.id}
          onChange={(event) => setWanted(event.target.value)}
          options={items.map((item) => ({
            value: item.id,
            label: `${item.name} · ${t('grades.view.weight', { weight: item.weight })} · ${t('gradebook.evaluations.scored', { scored: item.scoredCount, total: view.rows.length })}`,
          }))}
        />
      </div>
      <Grid
        key={evaluation.id}
        cell={cell}
        view={view}
        evaluationId={evaluation.id}
        evaluationName={evaluation.name}
      />
    </div>
  );
};
